import { queryCache, usePaginatedQuery, useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import {
  ADD_APPLICATION_CHAT_SUCCESS,
  ADD_APPLICATION_NOTES_SUCCESS,
  ApplicationDispatch,
  CLEAR_APPLICATION_CHATS,
  CLEAR_APPLICATION_REHASHES,
  CLEAR_APPLICATION_SCORE, 
  CLEAR_DOWN_PAYMENT_HISTORIES,
  CLEAR_PREVIOUS_AMS_LOANS,
  LOAD_APPLICATION_QUEUE_DATA,
  SET_SERVER_MESSAGE,
  UPDATE_APPLICATION_CHECKLIST_SUCCESS,
  UPDATE_APPLICATION_EXCEPTIONS_SUCCESS
} from "../store/application/types";
import { IApplicationListRow } from "../Interfaces/Application/IApplicationListRow";
import { IApplication } from "../Interfaces/Application/IApplication";
import { massageApp } from "../utils/appUtils";
import { fetchData, postData } from "./fetch";
import { ICustomField } from "../Interfaces/Common/ICustomField";
import { AppState } from '../store/rootReducer';
import { Constants } from "../utils/constants";
import { IAppStatus } from "../Interfaces/Application/IAppStatus";
import { IApplicationNotes } from '../Interfaces/Application/IApplicationNotes';
import { IApplicationChecklist } from "../Interfaces/Application/IApplicationChecklist";
import { IApplicationChat } from "../Interfaces/Application/IApplicationChat";
import { State } from "@progress/kendo-data-query";
import { IApplicationException } from "../Interfaces/Application/IApplicationException";
import { IApplicationExceptionsCustomField } from "../Interfaces/Application/IApplicationExceptionsCustomField";
import { reject } from "ramda";
import produce from "immer";
import { updateEditingApp } from "../store/application/actions";
import { BusinessRulesEngine } from "./BusinessRulesData";
import {MS_PER_MINUTE} from "@progress/kendo-date-math";

// export const queryApplicationConfiguration = async (
//   dispatch: ApplicationDispatch
// ) => {
//   try {
//     const url = `api/Application/Configuration`;
//     const amsApplicationUri: { amsApplicationUri: string } = await fetchData(
//       url,
//       dispatch
//     );
//     return amsApplicationUri;
//   } catch (err) {
//     dispatch({
//       type: SET_SERVER_MESSAGE,
//       payload: { isError: true, message: err as string },
//     });
//     return undefined;
//   }
// };

// export const useQueryApplicationConfiguration = () => {
//   const dispatch = useDispatch<ApplicationDispatch>();
//   return useQuery(
//     ["application/configuration"],
//     async () => await queryApplicationConfiguration(dispatch),
//     {
//       refetchOnWindowFocus: false,
//     }
//   );
// };

export const queryApplication = async (
  id: string,
  customFields: ICustomField[],
  dispatch: ApplicationDispatch,
  appStatus: IAppStatus
) => {
  // Clear these objects before it grabs the application data
  dispatch({ type: CLEAR_APPLICATION_CHATS });
  dispatch({ type: CLEAR_APPLICATION_SCORE });
  dispatch({ type: CLEAR_APPLICATION_REHASHES });
  dispatch({ type: CLEAR_PREVIOUS_AMS_LOANS });
  dispatch({ type: CLEAR_DOWN_PAYMENT_HISTORIES });
  // Return a new application if we are navigating to "new" application page.
  try {
    const url = `api/Application/GetApplication?id=${id}`;
    const data: IApplication = await fetchData(url, dispatch);
    const massagedApp = massageApp(data, customFields);
    return massagedApp;
  } catch (err) {
    dispatch({
      type: SET_SERVER_MESSAGE,
      payload: { isError: true, message: err as string },
    });
    return undefined;
  }
};

export const addApplicationNotes = async (
  applicationId: string,
  notes: { note: string, createdDate: Date }[],
  dispatch: ApplicationDispatch
) => {
  const url = `api/Application/addnotes/${applicationId}`;
  try {
    const applicationNotes: IApplicationNotes[] = await postData(url, notes);
    dispatch({ type: ADD_APPLICATION_NOTES_SUCCESS, payload: { notes: applicationNotes } });
  } catch (error) {
    const msg = `Unable to add new notes: ${error}`;
    dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: msg, isError: true } });
  }
}

export const addApplicationUnderwriterNotes = async (
  applicationId: string,
  employeeId: string,
  notes: { note: string, createdDate: Date, applicationNoteRoles: string[] | null }[],
  dispatch: ApplicationDispatch
) => {
  const url = `api/Application/addnotes/${applicationId}`;
  try {
    const applicationNotes: IApplicationNotes[] = await postData(url, notes);
    dispatch({ type: ADD_APPLICATION_NOTES_SUCCESS, payload: { notes: applicationNotes } });
  } catch (error) {
    const msg = `Unable to add new notes: ${error}`;
    dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: msg, isError: true } });
  }
}

export const addApplicationChat = async (
  applicationChat: IApplicationChat,
  dispatch: ApplicationDispatch
) => {
  const url = `api/ApplicationChat/AddApplicationChat`;
  try {
    const applicationChats: IApplicationChat = await postData(url, applicationChat);
    dispatch({ type: ADD_APPLICATION_CHAT_SUCCESS, payload: { applicationChat: applicationChats } });
  } catch (error) {
    const msg = `Unable to add new chat: ${error}`;
    dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: msg, isError: true } });
  }
}

export const updateApplicationExceptions = async (
  applicationExceptions: IApplicationException[],
  applicationId: string,
  undoExceptions: boolean,
  dispatch: ApplicationDispatch
) => {
  const url = `api/ApplicationException/UpdateApplicationExceptions/${undoExceptions}/${applicationId}`;
  try {
    const updatedApplicationExceptionsCustomField: IApplicationExceptionsCustomField = await postData(url, applicationExceptions);
    dispatch({ type: UPDATE_APPLICATION_EXCEPTIONS_SUCCESS, payload: { applicationExceptionsCustomField: updatedApplicationExceptionsCustomField } });
  } catch (error) {
    const msg = `Unable to update application exceptions: ${error}`;
    dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: msg, isError: true } });
  }
}

export const updateApplicationChecklist = async (
  editingApp: IApplication,
  applicationChecklist: IApplicationChecklist,
  businessRulesEngine: BusinessRulesEngine,
  dispatch: ApplicationDispatch
) => {
  const url = `api/ApplicationChecklist/updateapplicationchecklist`;
  try {
    const updatedApplicationChecklist: IApplicationChecklist = await postData(url, applicationChecklist);
    dispatch({ type: UPDATE_APPLICATION_CHECKLIST_SUCCESS, payload: { checklist: updatedApplicationChecklist } });
    dispatch({
      type: SET_SERVER_MESSAGE,
      payload: {
        message: `Checklist changes saved`,
        isError: false,
      },
    });

    const updatedApp = produce(editingApp, (updatedEditingApp) => {
      (updatedEditingApp as any).savedCount = ((updatedEditingApp as any).savedCount ?? 0) + 1;
      updatedEditingApp.applicationChecklists = updatedEditingApp.applicationChecklists.map(ac => {
        if (ac.id === updatedApplicationChecklist.id) {
          return updatedApplicationChecklist;
        }
        return ac;
      })
    });
    dispatch(updateEditingApp(updatedApp!, businessRulesEngine));

  } catch (error) {
    const msg = `Unable to update checklist: ${error}`;
    dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: msg, isError: true } });
  }
}

export const setQueryApplication = (app: IApplication) => {
  queryCache.setQueryData(["application", app.id], app);
};
export const useQueryApplication = (id: string) => {
  const dispatch = useDispatch<ApplicationDispatch>();
  const customFields = useSelector(
    (s: AppState) => s.applications.customFields
  );
  // Get new application status in case we need to create a new app.
  // const newApplicationData: INewApplicationData = {
  const appStatus = useSelector((state: AppState) =>
    state.applications.appStatuses.find(
      (as) => as.id === Constants.newApplicationStatusId
    )
  );
  // appCustomers: [NEW_APP_CUSTOMER],
  // };
  return useQuery(
    ["application", id],
    async () => await queryApplication(id, customFields, dispatch, appStatus!),
    {
      refetchOnWindowFocus: false,
      initialData: undefined,
      cacheTime: undefined,
      staleTime: undefined,
      isDataEqual: () => false,
    }
  );
};
// TODO: update to use new sort descriptors
export interface QueryApplicationList {
  dataState: State;
}

// List of field names (i.e. column names) for which we want to apply the date-range filter to.
const targetDateRangeFilterFieldNames: string[] = ["appDate", "latestExceptionDate"];

export const useQueryApplicationList = (applicationQueueId: string, count: number, params: QueryApplicationList) => {
  const dispatch = useDispatch<ApplicationDispatch>();
  const targetDateRangeFilter = params.dataState.filter?.filters?.find((filt: any) => targetDateRangeFilterFieldNames.includes(filt.field)) as any;
  // We need to manually reject/append data range filters before making the server call so that we can construct the correct filter object for server-side app filtering.
  let queryKey = params.dataState.filter ? {
    ...params,
    dataState: {
      ...params.dataState,
      filter: {
        ...params.dataState.filter,
        filters: reject((filt: any) => filt.field === "appDate" || filt.field === "latestExceptionDate", params.dataState.filter?.filters || []),
      }
    }
  } : params;

  // Append date-range filters depending on whether the user selected a start/end date(s).
  if (targetDateRangeFilter && targetDateRangeFilter.value && queryKey.dataState.filter) {
    if (targetDateRangeFilter.value.start) {
      // convert to utc before sending to server
      const start = targetDateRangeFilter.value.start; // moment.utc(targetDateRangeFilter.value.start).toISOString();
      queryKey.dataState.filter.filters.push({ field: targetDateRangeFilter.field, operator: 'gte', value: start });
    }
    if (targetDateRangeFilter.value.end) {
      // add one day to the end date to account for user's local timezone offset
      const end = targetDateRangeFilter.value.end; //moment(targetDateRangeFilter.value.end).add(1, 'days').toISOString();
      queryKey.dataState.filter.filters.push({ field: targetDateRangeFilter.field, operator: 'lte', value: end });
    }
  }
  
  return usePaginatedQuery([`api/Application/ApplicationQueue?queueId=${applicationQueueId}`, queryKey], async () => {
    try {
      dispatch({ type: LOAD_APPLICATION_QUEUE_DATA, payload: { applicationQueueId: applicationQueueId, count: count, loading: true } });
      let url = `api/Application/ApplicationQueue?queueId=${applicationQueueId}`;
      
      // TODO: pass in filter data
      const data = await postData(url, queryKey.dataState);
      // Get total applications count
      const total: number = data.total;
      // Get paginated/filtered list of applications.
      const applications: IApplicationListRow[] = data.result;
      dispatch({ type: LOAD_APPLICATION_QUEUE_DATA, payload: { applicationQueueId: applicationQueueId, count: data.total, loading: false } });
      return { total, applications };
    } catch (err: any) {
      dispatch({ type: LOAD_APPLICATION_QUEUE_DATA, payload: { applicationQueueId: applicationQueueId, count: 0, loading: false } });
      dispatch({
        type: SET_SERVER_MESSAGE,
        payload: { isError: true, message: err.message ?? "Unknown error" },
      });
      return { total: 0, applications: [] };
    }
  }, { refetchOnWindowFocus: false, refetchInterval: MS_PER_MINUTE * 2 });
};
